import React from 'react';
import { useSearchParams } from 'next/navigation';

import { Button } from '../Button';
import { Select } from '../Select';
import { TextInput } from '../TextInput';
import { Text } from '../Text';
import { Form, FormSection, FormRow, FormField } from '../Form';
import { FormProgressIndicator } from '../FormProgressIndicator';
import { useValidatedState } from '../../hooks/validated-state';

const defaultPlanType = 'individual';

export type PlanType = 'individual' | 'medicare' | 'dental' | 'group';

interface BasicsStepProps {
  hasAgent: boolean;
  onSubmit: (data: {
    planType: PlanType;
    isPlanChange: boolean;
    zipCode: string;
    firstName: string;
    lastName: string;
    phoneNumber: string;
    emailAddress: string;
  }) => void;
}

export const BasicsStep: React.FC<BasicsStepProps> = ({
  hasAgent,
  onSubmit,
}) => {
  const searchParams = useSearchParams();

  const [planType, setPlanType] = React.useState<PlanType>(defaultPlanType);

  const [isPlanChange, setIsPlanChange] = React.useState(false);

  const [
    zipCode,
    setZipCode,
    isZipCodeValid,
    shouldIndicateInvalidZipCode,
    setShouldIndicateInvalidZipCode,
  ] = useValidatedState(
    '',
    (zipCode) =>
      Boolean((zipCode || '').match(/^[0-9]{5}$/)) &&
      // Verify Zip Code is in North Carolina
      Number(zipCode) >= 27000 &&
      Number(zipCode) <= 28999,
  );

  const [
    firstName,
    setFirstName,
    isFirstNameValid,
    shouldIndicateInvalidFirstName,
    setShouldIndicateInvalidFirstName,
  ] = useValidatedState('', /^.+$/);

  const [
    lastName,
    setLastName,
    isLastNameValid,
    shouldIndicateInvalidLastName,
    setShouldIndicateInvalidLastName,
  ] = useValidatedState('', /^.+$/);

  const [
    phoneNumber,
    setPhoneNumber,
    isPhoneNumberValid,
    shouldIndicateInvalidPhoneNumber,
    setShouldIndicateInvalidPhoneNumber,
  ] = useValidatedState(
    '',
    /^(1\s?)?((\([0-9]{3}\))|[0-9]{3})[\s\-]?[\0-9]{3}[\s\-]?[0-9]{4}$/,
  );

  const [
    emailAddress,
    setEmailAddress,
    isEmailAddressValid,
    shouldIndicateInvalidEmailAddress,
    setShouldIndicateInvalidEmailAddress,
  ] = useValidatedState(
    '',
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  );

  const validationStates = [
    isZipCodeValid,
    isFirstNameValid,
    isLastNameValid,
    isPhoneNumberValid,
    isEmailAddressValid,
  ];

  const makeAllValidationErrorsVisible = () => {
    setShouldIndicateInvalidZipCode(true);
    setShouldIndicateInvalidFirstName(true);
    setShouldIndicateInvalidLastName(true);
    setShouldIndicateInvalidPhoneNumber(true);
    setShouldIndicateInvalidEmailAddress(true);
  };

  React.useEffect(() => {
    const queryParamPlanType = searchParams?.get('planType');
    // Set initial state for form fields after query fields become available
    setPlanType(
      queryParamPlanType === 'individual' ||
        queryParamPlanType === 'medicare' ||
        queryParamPlanType === 'dental' ||
        queryParamPlanType === 'group'
        ? queryParamPlanType
        : defaultPlanType,
    );
    setZipCode(searchParams?.get('zipCode') ?? '');
  }, [searchParams, setZipCode]);

  return (
    <Form
      onSubmit={() => {
        if (validationStates.some((isValid) => !isValid)) {
          makeAllValidationErrorsVisible();

          return;
        }

        onSubmit({
          planType,
          isPlanChange,
          zipCode,
          firstName,
          lastName,
          phoneNumber,
          emailAddress,
        });
      }}
    >
      <FormSection>
        <div className="justify-center mb-6">
          <FormProgressIndicator
            steps={[
              'Basics',
              planType === 'group' ? 'Company' : 'Applicants',
              'Quote',
            ]}
            currentStepIndex={0}
          />
        </div>
        {/* For agent quote pages we're adding a agent header which contains an H1 */}
        {hasAgent ? (
          <Text.H4>Let&apos;s get you started.</Text.H4>
        ) : (
          <Text.H1 className="!text-xl md:!text-2xl">
            Let&apos;s get you started.
          </Text.H1>
        )}
      </FormSection>
      <FormSection>
        <FormRow>
          <FormField labelText="Plan Type">
            <Select
              options={[
                { value: 'individual', label: 'Individual & Family' },
                { value: 'medicare', label: 'Medicare' },
                { value: 'dental', label: 'Dental' },
                { value: 'group', label: 'Group' },
              ]}
              value={planType}
              onChange={setPlanType}
            />
          </FormField>
          <FormField
            labelText="ZIP Code"
            error={
              shouldIndicateInvalidZipCode
                ? 'Please enter a valid North Carolina ZIP code.'
                : undefined
            }
          >
            <TextInput
              value={zipCode}
              onChange={setZipCode}
              placeholder="ZIP Code"
              maxLength={5}
              inputMode="numeric"
              autoFocus={!searchParams?.get('zipCode')}
            />
          </FormField>
        </FormRow>
        <hr className="mt-4 mb-6" />
        <FormRow>
          <FormField
            labelText="First Name"
            error={
              shouldIndicateInvalidFirstName
                ? 'Please enter your first name.'
                : ''
            }
          >
            <TextInput
              value={firstName}
              onChange={setFirstName}
              autoFocus={!!searchParams?.get('zipCode')}
            />
          </FormField>
          <FormField
            labelText="Last Name"
            error={
              shouldIndicateInvalidLastName
                ? 'Please enter your last name.'
                : ''
            }
          >
            <TextInput value={lastName} onChange={setLastName} />
          </FormField>
        </FormRow>
        <FormRow>
          <FormField
            labelText="Email"
            error={
              shouldIndicateInvalidEmailAddress
                ? 'Please enter your email address.'
                : ''
            }
          >
            <TextInput value={emailAddress} onChange={setEmailAddress} />
          </FormField>
          <FormField
            labelText="Phone Number"
            error={
              shouldIndicateInvalidPhoneNumber
                ? 'Please enter a valid US phone number.'
                : ''
            }
          >
            <TextInput value={phoneNumber} onChange={setPhoneNumber} />
          </FormField>
        </FormRow>
        <FormRow>
          <p className="text-xs max-w-prose">
            By clicking Continue, I authorize Health Plans of NC to contact me
            by phone or text message. I understand that I may opt-out of
            communication at any time.
          </p>
        </FormRow>
        <FormRow>
          <div className="flex w-full justify-end">
            <Button
              variant="PRIMARY"
              size="LARGE"
              type="submit"
              is="input"
              value="Continue"
            />
          </div>
        </FormRow>
      </FormSection>
    </Form>
  );
};
